import React from 'react';
import { Globe } from 'lucide-react';
import { PANEL, colorAt } from './chartTheme';

function formatPath(path) {
  if (path === '/') return 'Home';
  return path.split('/').filter(Boolean).map(p => p.charAt(0).toUpperCase() + p.slice(1).replace(/-/g, ' ')).join(' / ');
}

function ListPanel({ title, testId, items, empty, children }) {
  return (
    <div className={PANEL} data-testid={testId}>
      <h3 className="font-display text-sm font-bold text-earth-dark mb-3">{title}</h3>
      {items.length > 0 ? children : <p className="text-xs text-earth/30 font-sans py-4 text-center">{empty}</p>}
    </div>
  );
}

export function TopPages({ pages }) {
  const maxViews = pages[0]?.views || 1;
  return (
    <ListPanel title="Top Pages" testId="top-pages" items={pages} empty="No page view data yet">
      <div className="space-y-2">
        {pages.map((p, i) => (
          <div key={p.path} className="group">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-earth/70 font-sans truncate flex-1 mr-2">{formatPath(p.path)}</span>
              <span className="text-xs text-earth-dark font-sans font-semibold flex-shrink-0">{p.views}</span>
            </div>
            <div className="h-1.5 bg-cream rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(p.views / maxViews) * 100}%`, backgroundColor: colorAt(i) }} />
            </div>
          </div>
        ))}
      </div>
    </ListPanel>
  );
}

export function TopProducts({ products }) {
  return (
    <ListPanel title="Most Viewed Products" testId="top-products-analytics" items={products} empty="No product views yet">
      <div className="space-y-3">
        {products.map((p, i) => (
          <div key={p.path} className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-display text-xs font-bold" style={{ backgroundColor: colorAt(i) }}>
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-earth-dark font-sans font-medium capitalize truncate">{p.path.replace('/products/', '').replace(/-/g, ' ')}</p>
              <p className="text-[10px] text-earth/40 font-sans">{p.views} views</p>
            </div>
          </div>
        ))}
      </div>
    </ListPanel>
  );
}

export function TopBlogs({ blogs }) {
  return (
    <ListPanel title="Most Read Blog Posts" testId="top-blogs-analytics" items={blogs} empty="No blog views yet">
      <div className="space-y-2.5">
        {blogs.map(b => (
          <div key={b.path} className="flex items-center justify-between">
            <span className="text-xs text-earth/70 font-sans truncate flex-1 mr-2 capitalize">{b.path.replace('/blog/', '').replace(/-/g, ' ')}</span>
            <span className="text-xs text-saffron font-sans font-semibold flex-shrink-0">{b.views}</span>
          </div>
        ))}
      </div>
    </ListPanel>
  );
}

export function TopReferrers({ referrers }) {
  return (
    <ListPanel title="Top Referrers" testId="top-referrers" items={referrers} empty="No referrer data yet">
      <div className="space-y-2">
        {referrers.map(r => (
          <div key={r.referrer} className="flex items-center gap-2">
            <Globe size={10} className="text-saffron/50 flex-shrink-0" />
            <span className="text-xs text-earth/60 font-sans truncate flex-1">{r.referrer}</span>
            <span className="text-xs text-earth-dark font-sans font-semibold">{r.count}</span>
          </div>
        ))}
      </div>
    </ListPanel>
  );
}
